import { useState } from "react";

const LessonPane = ({
  lessonHtml,
  t,
  originalBotMsg,
  originalBotMsgIndex,
  onToggleStats,
  onDownload,
  onExpand,
  title,
  showChatStats,
  hasQuestions,
  onMoveToQuestions,
  loading,
  isWorkspaceExpanded,
  onToggleWorkspaceExpand,
}) => {
  const [zoomLevel, setZoomLevel] = useState(1.0);

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.1, 1.8));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.1, 0.7));
  };

  const handleZoomReset = () => {
    setZoomLevel(1.0);
  };

  const stripInlineFonts = (html) => {
    if (!html) return "";
    return html
      .replace(/font-size\s*:\s*([^;'">]+|'[^']*'|"[^"]*")+;?/gi, "")
      .replace(/font-family\s*:\s*([^;'">]+|'[^']*'|"[^"]*")+;?/gi, "")
      .replace(/line-height\s*:\s*([^;'">]+|'[^']*'|"[^"]*")+;?/gi, "");
  };

  const estimateReadingTime = (html) => {
    if (!html) return 0;
    const text = html.replace(/<[^>]*>?/gm, "");
    const words = text.trim().split(/\s+/).filter((w) => w.length > 0);
    const minutes = Math.ceil(words.length / 180);
    return Math.max(1, minutes);
  };

  const getTextStats = (html) => {
    if (!html) return { words: 0, characters: 0 };
    const text = html.replace(/<[^>]*>?/gm, "").trim();
    const words = text.split(/\s+/).filter((w) => w.length > 0).length;
    const characters = text.replace(/\s+/g, "").length;
    return { words, characters };
  };

  const readingTime = estimateReadingTime(lessonHtml);
  const totalTimeMs = originalBotMsg
    ? (originalBotMsg.databaseTimeMs || 0) + (originalBotMsg.apiTimeMs || 0)
    : 0;
  const totalTimeSec = (totalTimeMs / 1000).toFixed(2);
  const modelName = originalBotMsg
    ? originalBotMsg.model || originalBotMsg.Model || "deepseek"
    : "deepseek";

  const stats = getTextStats(lessonHtml);

  return (
    <div className="lesson-pane-container h-100 d-flex flex-column">
      <div className="lesson-header d-flex align-items-center justify-content-between p-3 border-bottom flex-wrap gap-2">
        <h3 className="lesson-header-title h6 font-weight-bold m-0 d-flex align-items-center text-right">
          <i className="fal fa-book-open text-primary ml-2"></i>
          <span>{title || t("Pages.Chatbot.LessonPaneTitle")}</span>
        </h3>
        <div className="lesson-header-tools d-flex align-items-center flex-wrap gap-2">
          {lessonHtml && (
            <div className="estimated-time-badge d-flex align-items-center px-2 py-1 rounded bg-light text-muted small ml-2">
              <i className="fal fa-clock ml-1"></i>
              <span>
                {readingTime}{" "}
                {readingTime > 1
                  ? t("Pages.Chatbot.Minutes")
                  : t("Pages.Chatbot.Minute")}{" "}
                {t("Pages.Chatbot.EstimatedReadingTime")}
              </span>
            </div>
          )}
          <div className="lesson-zoom-toolbar">
            <button
              type="button"
              className="zoom-btn btn btn-sm btn-link text-muted p-1"
              onClick={handleZoomIn}
              title={t("Pages.Missions.BookItem.tool.zoomIn")}
            >
              <i className="fal fa-plus"></i>
            </button>
            <button
              type="button"
              className="zoom-btn btn btn-sm btn-link text-muted p-1"
              onClick={handleZoomReset}
              title={t("Pages.Chatbot.Reset")}
            >
              <i className="fal fa-history"></i>
            </button>
            <button
              type="button"
              className="zoom-btn btn btn-sm btn-link text-muted p-1"
              onClick={handleZoomOut}
              title={t("Pages.Missions.BookItem.tool.zoomOut")}
            >
              <i className="fal fa-minus"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="lesson-body-scroll flex-grow-1 p-3 p-md-4 overflow-auto">
        {loading && !lessonHtml ? (
          <div className="lesson-empty-state text-center text-muted py-5">
            <i
              className="fal fa-spinner fa-spin empty-icon d-block mb-3 h3"
              style={{ color: "#92278f" }}
            ></i>
            <p>{t("Pages.Chatbot.LoadingLesson")}</p>
          </div>
        ) : lessonHtml ? (
          <>
            <div
              className="lesson-content-html bot-answer-html text-right"
              style={{ fontSize: `${zoomLevel}rem`, lineHeight: "1.8" }}
              dangerouslySetInnerHTML={{ __html: stripInlineFonts(lessonHtml) }}
            />
            <div className="lesson-text-info-bar">
              <span>
                <i className="fal fa-file-alt"></i>
                <span>
                  {t("Pages.Chatbot.WordsCount")}: {stats.words}
                </span>
              </span>
              <span>
                <i className="fal fa-font"></i>
                <span>
                  {t("Pages.Chatbot.CharactersCount")}: {stats.characters}
                </span>
              </span>
            </div>
            <div className="bot-bubble-actions-row mb-3">
              {showChatStats && totalTimeMs > 0 && onToggleStats && (
                <div
                  className="stats-badge total-time-badge"
                  onClick={() => onToggleStats(originalBotMsgIndex)}
                  role="button"
                  tabIndex="0"
                  title={t("Pages.Chatbot.TotalTime")}
                  style={{ gap: "8px" }}
                >
                  <i className="fal fa-robot"></i>
                  <span style={{ textTransform: "capitalize", fontWeight: "bold" }}>
                    {modelName}
                  </span>
                  <span style={{ opacity: 0.3 }}>|</span>
                  <i className="fal fa-clock"></i>
                  <span>
                    {t("Pages.Chatbot.TotalTime")}: {totalTimeSec}{" "}
                    {t("Pages.Chatbot.Sec")}
                  </span>
                </div>
              )}
              <div className="bot-bubble-actions">
                {onExpand && (
                  <button
                    type="button"
                    className="fullscreen-expand-btn"
                    onClick={() => onExpand(lessonHtml)}
                    title={t("Pages.Chatbot.FullScreen")}
                  >
                    <i className="fal fa-expand-arrows"></i>
                  </button>
                )}
                {onDownload && (
                  <button
                    type="button"
                    className="bubble-download-btn"
                    onClick={() => onDownload(lessonHtml, title)}
                    title={t("Pages.Chatbot.Download")}
                  >
                    <i className="fal fa-download"></i>
                  </button>
                )}
              </div>
            </div>
            {hasQuestions && onMoveToQuestions && (
              <div className="lesson-actions-footer d-flex justify-content-center mt-4 border-top pt-3">
                <button
                  className="move-to-questions-btn btn btn-success btn-lg rounded-pill px-4 d-inline-flex align-items-center gap-2 shadow-sm"
                  onClick={onMoveToQuestions}
                >
                  <span>{t("Pages.Chatbot.StartSolvingQuestions") || "البدء في حل الأسئلة التفاعلية"}</span>
                  <i className="fal fa-arrow-left"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="lesson-empty-state text-center text-muted py-5">
            <i className="fal fa-graduation-cap empty-icon d-block mb-3 h2 text-muted"></i>
            <p>{t("Pages.Chatbot.NoInfo")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonPane;
